import { useState, type ReactNode } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import {
  presenceCandidate,
  presenceLinks,
  presenceNextStep,
  presenceVisible,
  type PresenceAttention,
  type PresenceLink,
} from '../../../lib/presence';
import { updateSpaceBinding, useSpaceBindings } from '../../../lib/space-bindings';
import { spaceBindingUpdateError } from '../../../lib/space-resources';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useSitePaneFits } from '../../../lib/use-site-pane';
import { siteRefOf, useSiteEditRunner } from '../site-review/use-site-edit';
import { headerPresence, presenceStrip, type PresenceAction } from '../../../lib/presence-checklist';
import { presentWebsite, type WorkspaceWebsite } from '../../../lib/workspace-website';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button, Card, TextButton, Toast, Typography } from '../ui';
import { presenceActionChat } from './PresenceChecklist';
import { PresenceGlyph } from './PresenceGlyph';
import { PresenceManage } from './PresenceManage';
import { SiteStrip } from './SiteStrip';
import { usePresenceChat, usePresenceLabel } from './use-presence-chat';
import styles from './SpacePresence.module.css';

// The Space's online presence, in its header (design Option C): under the title, a quiet line — the main website as
// a globe + host, one small round mark per other presence (a dot when it needs attention), then "Manage"; on the
// right, ONE next-step pill; and, for a suggested website, one quiet "Is {name} this Space's website? Yes · Not this
// one" line under the header. It replaced the Space "site bar" card. Personal, and any Space with no presence, gets
// nothing at all.
//
// Every rule is `lib/presence.ts` (unit-tested, mirrored by iOS `Presence.swift` and Android `Presence.kt`); this file
// only draws it. Manage opens `PresenceManage`.
//
// When the server describes the Space (`headerPresence`: `WorkspaceSummary.presence`, else the older
// `WorkspaceWebsite.presence`), the header is the SITE STRIP instead (`SiteStrip`, rules `presenceStrip` in
// lib/presence-checklist.ts): Site · Plan · Domain · Business email · Visits · On Wix cells with one accent, then an
// outlined Manage that opens the Online presence card. Without `presence` (an older server) it is the line, pill and
// Manage above.

function Dot({ attention }: { attention: PresenceAttention }) {
  const tone = attention === 'RECONNECT' || attention === 'NOT_OWNED' ? styles.dotCritical : styles.dotWarning;
  return <span className={`${styles.dot} ${tone}`} aria-hidden />;
}

/**
 * The presence's three pieces, for the Space header to place: `line` under the title, `pill` in the title row's
 * actions slot, `candidate` under the header — each null when it has nothing to say. `strip` marks the site strip,
 * carried in `pill` and drawn as its own row under the header (never in the title row, the Agents island's corner). They appear together once the
 * bindings read settles, so the header never changes shape after it lands.
 */
export function useSpacePresence(
  workspace: WorkspaceSummary | undefined,
  pageName: string,
): { line: ReactNode; pill: ReactNode; candidate: ReactNode; strip?: boolean } {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const bindings = useSpaceBindings(workspace?.id);
  const workspaceId = workspace?.id ?? '';
  const workspaceName = workspace?.name ?? '';
  const openChat = usePresenceChat(workspaceId, workspaceName);
  const labelOf = usePresenceLabel();
  const [manageOpen, setManageOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const phone = useIsPhone();
  const paneFits = useSitePaneFits();
  const website0 = workspace ? presentWebsite(workspace.website) : null;
  const runSiteEdit = useSiteEditRunner({ workspaceId, workspaceName, website: website0, siteRef: siteRefOf(website0) });

  const none = { line: null, pill: null, candidate: null };
  if (!workspace || bindings.isLoading) return none;
  const website = presentWebsite(workspace.website);
  const data = bindings.data;
  const links = presenceLinks(website, data);
  const mainLinkUrl = links.find((l) => l.main)?.url ?? '';
  const header = presenceStrip(headerPresence(workspace), { spaceName: workspaceName, siteUrl: mainLinkUrl, phone, paneFits });
  if (!header && !presenceVisible(website, data)) return none;

  const click = (button: string) => logPageClickBi({ page_name: pageName, button_name: button });
  const openManage = () => {
    click('presence_manage');
    setManageOpen(true);
  };
  const step = presenceNextStep(website, data);
  const suggested = presenceCandidate(website, data);

  const answer = async (update: 'CONFIRM' | 'DISMISS', bindingId: string) => {
    if (busy) return;
    click(update === 'CONFIRM' ? 'presence_confirm_site' : 'presence_dismiss_site');
    setBusy(true);
    try {
      const error = spaceBindingUpdateError(await updateSpaceBinding({ workspaceId, action: update, bindingId }));
      if (error) setToast(t(error));
    } finally {
      setBusy(false);
    }
  };

  const main = links.find((l) => l.main);
  const others = links.filter((l) => !l.main);


  // A mark with no public page (a site not live yet, a profile with no link) opens Manage instead.
  const mainLink = main ? (
    main.url ? (
      <a
        key="main"
        href={main.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.mainLink} sym-p3`}
        aria-label={main.attention ? labelOf({ ...main, providerName: '' }) : undefined}
        data-testid="spacePresenceMain"
        onClick={() => click('presence_link_open')}
      >
        <Globe size={14} aria-hidden />
        <span className={styles.host}>{main.name}</span>
        {main.attention && <Dot attention={main.attention} />}
      </a>
    ) : (
      <TextButton
        key="main"
        size="small"
        className={styles.mainButton}
        startIcon={<Globe size={14} aria-hidden />}
        aria-label={labelOf({ ...main, providerName: '' })}
        data-testid="spacePresenceMain"
        onClick={openManage}
      >
        <span className={styles.host}>{main.name}</span>
        {main.attention && <Dot attention={main.attention} />}
      </TextButton>
    )
  ) : null;

  const mark = (link: PresenceLink) => {
    const inner = (
      <>
        <PresenceGlyph glyph={link.glyph} size={14} />
        {link.attention && <Dot attention={link.attention} />}
      </>
    );
    return link.url ? (
      <a
        key={link.id}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.mark}
        aria-label={labelOf(link)}
        title={labelOf(link)}
        data-testid={`spacePresenceLink-${link.id}`}
        onClick={() => click('presence_link_open')}
      >
        {inner}
      </a>
    ) : (
      <button
        key={link.id}
        type="button"
        className={styles.mark}
        aria-label={labelOf(link)}
        title={labelOf(link)}
        data-testid={`spacePresenceLink-${link.id}`}
        onClick={openManage}
      >
        {inner}
      </button>
    );
  };

  const line =
    links.length > 0 ? (
      <div className={styles.line} data-testid="spacePresenceLine">
        {mainLink}
        {main && others.length > 0 && (
          <span className={styles.separator} aria-hidden>
            ·
          </span>
        )}
        {others.map(mark)}
        {data && (
          <TextButton size="small" className={styles.manage} data-testid="spacePresenceManage" onClick={openManage}>
            {t('workspace.siteBar.manage')}
          </TextButton>
        )}
        {data && (
          <PresenceManage
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            website={website}
            bindings={data}
            pageName={pageName}
          />
        )}
        <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
      </div>
    ) : null;

  const pill = step ? (
    <Card variant="outline" size="sm" disablePadding className={styles.pill} data-testid="spacePresenceNextStep" data-step={step.kind}>
      <Typography variant="p3" color="primary" component="span" className={styles.pillText}>
        {t(step.text.key, step.text.values)}
      </Typography>
      <Button
        color="accent"
        size="small"
        onClick={() => {
          click(step.bi);
          openChat(step.chat);
        }}
      >
        {t(step.actionKey)}
      </Button>
    </Card>
  ) : null;

  const candidate = suggested ? (
    <div className={styles.candidate} data-testid="spacePresenceCandidate">
      <Typography variant="p3" color="secondary" component="span">
        {t('workspace.siteBar.candidateQuestion', { name: suggested.name })}
      </Typography>
      <TextButton size="small" className={styles.manage} disabled={busy} onClick={() => void answer('CONFIRM', suggested.bindingId)}>
        {t('workspace.siteBar.confirmYes')}
      </TextButton>
      <span className={styles.separator} aria-hidden>
        ·
      </span>
      <TextButton size="small" className={styles.manage} disabled={busy} onClick={() => void answer('DISMISS', suggested.bindingId)}>
        {t('workspace.siteBar.notThisOne')}
      </TextButton>
      {/* With no line above it, the answer's error toast and Manage live here. */}
      {links.length === 0 && !header && <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />}
    </div>
  ) : null;

  if (header) {
    // The site a header chat is about: the tile's, else the main link's; none on a Space with no website yet.
    const chatSite: WorkspaceWebsite | null = website ?? (main ? { name: main.name, url: main.url } : null);
    const act = (a: PresenceAction) => {
      click(a.bi);
      if (runSiteEdit(a)) return;
      if (a.target.kind === 'chat') openChat(presenceActionChat(a.target.message, chatSite));
    };
    const strip = (
      <>
        <SiteStrip strip={header} onAct={act} onManage={data ? openManage : undefined} />
        {data && (
          <PresenceManage
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            website={website}
            bindings={data}
            pageName={pageName}
            spacePresence={headerPresence(workspace)}
          />
        )}
        <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
      </>
    );
    return { line: null, pill: strip, candidate, strip: true };
  }

  return { line, pill, candidate };
}
